import { useMemo, useCallback } from 'react';
import { Button, Slider, Select, Tooltip } from 'antd';
import {
  PlayCircleOutlined,
  PauseCircleOutlined,
  StepBackwardOutlined,
  StepForwardOutlined,
  FastBackwardOutlined,
  FastForwardOutlined,
  PushpinOutlined,
  AimOutlined,
  ColumnHeightOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { usePlayer } from './usePlayerContext';
import { useEventSelection } from '../../contexts/useEventSelection';
import { useTheme } from '../../contexts/useTheme';
import { isEventActive } from '../../utils/eventSource';
import { getClosestMinuteOffsetToNow } from './generateMinuteClock';

interface DashboardPlayerProps {
  loading?: boolean;
  compact?: boolean;
  onCompactChange?: (compact: boolean) => void;
}

dayjs.extend(utc);
dayjs.extend(timezone);

const { Option } = Select;

type TemporalState = 'past' | 'now' | 'future';

const NOW_FUTURE_THRESHOLD_SECONDS = 90;
const NOW_PAST_THRESHOLD_SECONDS = 150;

function getTemporalState(displayTime: dayjs.Dayjs): TemporalState {
  const nowUtc = dayjs.utc();
  const displayUtc = displayTime.utc();
  const diffSeconds = displayUtc.diff(nowUtc, 'second');
  if (diffSeconds > NOW_FUTURE_THRESHOLD_SECONDS) return 'future';
  if (diffSeconds < -NOW_PAST_THRESHOLD_SECONDS) return 'past';
  return 'now';
}

function getTemporalColor(state: TemporalState, isDark: boolean): string {
  switch (state) {
    case 'past':
      return isDark ? 'rgba(255, 255, 255, 0.55)' : 'rgba(0, 0, 0, 0.45)';
    case 'now':
      return isDark ? '#95de64' : '#389e0d';
    case 'future':
      return isDark ? '#69c0ff' : '#096dd9';
  }
}

const COMPACT_SLIDER_CSS = `
  .dashboard-player-compact .ant-slider { margin: 4px 0; }
  .dashboard-player-compact .ant-slider-rail,
  .dashboard-player-compact .ant-slider-track { height: 3px !important; }
  .dashboard-player-compact .ant-slider-handle { width: 10px !important; height: 10px !important; }
  .dashboard-player-compact .ant-slider-handle::after { width: 10px !important; height: 10px !important; }
  .dashboard-player-compact .ant-select-selector { height: 22px !important; font-size: 10px !important; }
  .dashboard-player-compact .ant-select-selection-item { line-height: 20px !important; font-size: 10px !important; }
  .dashboard-player-compact .ant-btn-sm { height: 22px !important; width: 22px !important; min-width: 22px !important; padding: 0 !important; font-size: 11px !important; }
`;

export const DashboardPlayer = ({
  loading = false,
  compact = false,
  onCompactChange,
}: DashboardPlayerProps) => {
  const {
    currentTimestamp,
    isPlaying,
    playbackSpeed,
    clockStart,
    clockMinuteCount,
    currentIndex,
    autoLoop,
    isLiveMode,
    playbackDock,
    togglePlayPause,
    setCurrentIndex,
    setPlaybackSpeed,
    setAutoLoop,
    stepForward,
    stepBackward,
    setPlaybackDock,
  } = usePlayer();
  const { selectedEvent } = useEventSelection();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const eventActive = isEventActive(selectedEvent);
  const showDockControls = eventActive && isLiveMode;

  const hasClock = clockMinuteCount > 0;

  const handleDockFirst = useCallback(() => {
    setPlaybackDock('first');
    setCurrentIndex(0);
  }, [setPlaybackDock, setCurrentIndex]);

  const handleDockCurrent = useCallback(() => {
    setPlaybackDock('current');
  }, [setPlaybackDock]);

  const handleDockNow = useCallback(() => {
    setPlaybackDock('now');
    if (!clockStart || clockMinuteCount === 0) return;
    setCurrentIndex(getClosestMinuteOffsetToNow(clockStart, clockMinuteCount));
  }, [setPlaybackDock, setCurrentIndex, clockStart, clockMinuteCount]);

  const handleDockLast = useCallback(() => {
    setPlaybackDock('last');
    setCurrentIndex(clockMinuteCount - 1);
  }, [setPlaybackDock, setCurrentIndex, clockMinuteCount]);

  const circuitTz = selectedEvent?.circuit?.time_zone || 'UTC';
  const browserTz = useMemo(() => dayjs.tz.guess(), []);
  const showBrowserTime = browserTz !== circuitTz;

  const currentDisplayTime = useMemo(() => {
    if (!currentTimestamp) return null;
    return dayjs(currentTimestamp).tz(circuitTz);
  }, [currentTimestamp, circuitTz]);

  const temporalState = currentDisplayTime
    ? getTemporalState(currentDisplayTime)
    : null;
  const timeColor = temporalState
    ? getTemporalColor(temporalState, isDark)
    : undefined;

  const borderColor = isDark
    ? 'rgba(255, 255, 255, 0.08)'
    : 'rgba(0, 0, 0, 0.08)';
  const mutedColor = isDark
    ? 'rgba(255, 255, 255, 0.5)'
    : 'rgba(0, 0, 0, 0.4)';

  const sectionHeight = compact ? 26 : 36;
  const sectionStyle: React.CSSProperties = {
    backgroundColor: isDark
      ? 'rgba(255, 255, 255, 0.06)'
      : 'rgba(255, 255, 255, 0.8)',
    border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'}`,
    borderRadius: compact ? 3 : 4,
    padding: compact ? '0 6px' : '0 8px',
    boxShadow: isDark ? 'none' : '0 1px 2px rgba(0, 0, 0, 0.04)',
    height: sectionHeight,
    boxSizing: 'border-box' as const,
  };

  const dockBtnStyle: React.CSSProperties = compact
    ? { minWidth: 22, padding: 0 }
    : { minWidth: 28, padding: '4px 6px' };

  return (
    <div
      className={compact ? 'dashboard-player-compact' : undefined}
      style={{
        flexShrink: 0,
        padding: '0',
        backgroundColor: isDark
          ? 'rgba(0, 0, 0, 0.25)'
          : 'rgba(0, 0, 0, 0.02)',
        borderBottom: `1px solid ${borderColor}`,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        minHeight: sectionHeight,
        transition: 'min-height 0.15s ease',
      }}
    >
      <style>{`
        @keyframes dashboard-ball-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        ${compact ? COMPACT_SLIDER_CSS : ''}
      `}</style>
      {/* Transport + slider */}
      <div
        style={{
          ...sectionStyle,
          flex: 1,
          minWidth: 0,
          display: 'flex',
          alignItems: 'center',
          gap: compact ? 4 : 6,
        }}
      >
        {!hasClock && (
          <span style={{ fontSize: compact ? 10 : 11, color: mutedColor, flex: 1 }}>
            Select a time range to enable playback
          </span>
        )}

        {/* Dock controls (live mode only) */}
        {hasClock && showDockControls && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
            <Tooltip title={playbackDock === 'first' ? 'Dock to first tile on refresh (active)' : 'Dock to first tile on refresh'}>
              <Button
                type={playbackDock === 'first' ? 'primary' : 'default'}
                size="small"
                icon={<FastBackwardOutlined />}
                onClick={handleDockFirst}
                style={dockBtnStyle}
              />
            </Tooltip>
            <Tooltip title={playbackDock === 'current' ? 'Stay on current tile on refresh (active)' : 'Stay on current tile on refresh'}>
              <Button
                type={playbackDock === 'current' ? 'primary' : 'default'}
                size="small"
                icon={<PushpinOutlined />}
                onClick={handleDockCurrent}
                style={dockBtnStyle}
              />
            </Tooltip>
            <Tooltip title={playbackDock === 'now' ? 'Dock to current time on refresh (active)' : 'Dock to current time on refresh'}>
              <Button
                type={playbackDock === 'now' ? 'primary' : 'default'}
                size="small"
                icon={<AimOutlined />}
                onClick={handleDockNow}
                style={{
                  ...dockBtnStyle,
                  ...(playbackDock === 'now' && temporalState !== 'now' ? {
                    animation: 'dashboard-ball-spin 2s ease-in-out infinite',
                  } : {}),
                }}
              />
            </Tooltip>
          </div>
        )}

        {hasClock && (<>
          {/* Transport controls */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: compact ? 2 : 3,
              flexShrink: 0,
            }}
          >
            <Button
              icon={<StepBackwardOutlined />}
              onClick={stepBackward}
              disabled={currentIndex === 0}
              size="small"
            />
            <Button
              type="primary"
              icon={
                isPlaying ? <PauseCircleOutlined /> : <PlayCircleOutlined />
              }
              onClick={togglePlayPause}
              size="small"
            />
            <Button
              icon={<StepForwardOutlined />}
              onClick={stepForward}
              disabled={currentIndex === clockMinuteCount - 1}
              size="small"
            />
          </div>

          {/* Slider */}
          <div style={{ flex: 1, minWidth: 0, marginLeft: compact ? 6 : 0 }}>
            <Slider
              min={0}
              max={clockMinuteCount - 1}
              value={currentIndex}
              onChange={setCurrentIndex}
              tooltip={{
                formatter: () => {
                  if (!currentTimestamp) return '';
                  return dayjs(currentTimestamp).tz(circuitTz).format('HH:mm');
                },
              }}
            />
          </div>

          {/* Speed */}
          <Select
            value={playbackSpeed}
            onChange={setPlaybackSpeed}
            size="small"
            style={{ width: compact ? 68 : 72, flexShrink: 0, fontSize: compact ? 10 : undefined }}
          >
            <Option value={1}>1 fps</Option>
            <Option value={2}>2 fps</Option>
            <Option value={5}>5 fps</Option>
          </Select>

          {/* Loop toggle */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: compact ? 3 : 4,
              flexShrink: 0,
            }}
          >
            {!compact && (
              <span style={{ fontSize: 11, color: mutedColor }}>Loop</span>
            )}
            <Tooltip title={compact ? (autoLoop ? 'Disable loop' : 'Enable loop') : undefined}>
              <button
                onClick={() => setAutoLoop(!autoLoop)}
                style={{
                  width: compact ? 26 : 32,
                  height: compact ? 14 : 18,
                  borderRadius: compact ? 7 : 9,
                  border: 'none',
                  backgroundColor: autoLoop
                    ? '#1890ff'
                    : isDark
                      ? 'rgba(255, 255, 255, 0.2)'
                      : 'rgba(0, 0, 0, 0.25)',
                  cursor: 'pointer',
                  position: 'relative',
                  transition: 'background-color 0.2s',
                }}
                title={autoLoop ? 'Disable loop' : 'Enable loop'}
              >
                <div
                  style={{
                    width: compact ? 10 : 14,
                    height: compact ? 10 : 14,
                    borderRadius: '50%',
                    backgroundColor: '#fff',
                    position: 'absolute',
                    top: 2,
                    left: autoLoop ? (compact ? 14 : 16) : 2,
                    transition: 'left 0.2s',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
                  }}
                />
              </button>
            </Tooltip>
          </div>

          {/* Frame counter */}
          <span
            style={{
              fontSize: compact ? 10 : 11,
              whiteSpace: 'nowrap',
              color: mutedColor,
              flexShrink: 0,
            }}
          >
            {currentIndex + 1}/{clockMinuteCount}
          </span>

          {/* Dock last (live mode only) */}
          {showDockControls && (
            <Tooltip title={playbackDock === 'last' ? 'Dock to last tile on refresh (active)' : 'Dock to last tile on refresh'}>
              <Button
                type={playbackDock === 'last' ? 'primary' : 'default'}
                size="small"
                icon={<FastForwardOutlined />}
                onClick={handleDockLast}
                style={dockBtnStyle}
              />
            </Tooltip>
          )}
        </>)}
      </div>

      {/* Time display */}
      {currentDisplayTime && timeColor && (
        <div
          style={{
            ...sectionStyle,
            display: 'flex',
            alignItems: 'center',
            gap: compact ? 4 : 6,
            flexShrink: 0,
          }}
        >
          <span
            style={{
              fontSize: compact ? 13 : 20,
              fontWeight: compact ? 600 : 'bold',
              color: timeColor,
              transition: 'color 0.3s ease',
              lineHeight: 1,
            }}
          >
            {currentDisplayTime.format('HH:mm')}
          </span>
          {showBrowserTime && (
            <span
              style={{
                fontSize: compact ? 10 : 12,
                color: mutedColor,
                whiteSpace: 'nowrap',
              }}
            >
              ({currentDisplayTime.tz(browserTz).format('HH:mm')})
            </span>
          )}
          {temporalState === 'now' && (
            <span
              style={{
                fontSize: compact ? 8 : 9,
                fontWeight: 700,
                backgroundColor: '#52c41a',
                color: '#fff',
                padding: compact ? '0px 4px' : '1px 5px',
                borderRadius: compact ? 2 : 3,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                lineHeight: compact ? '14px' : undefined,
              }}
            >
              LIVE
            </span>
          )}
        </div>
      )}

      {/* Compact toggle */}
      {onCompactChange && (
        <div
          style={{
            ...sectionStyle,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0,
            width: sectionHeight,
            flexShrink: 0,
          }}
        >
          <Tooltip title={compact ? 'Expand player' : 'Compact player'}>
            <Button
              type="text"
              size="small"
              icon={<ColumnHeightOutlined />}
              onClick={() => onCompactChange(!compact)}
              aria-label={compact ? 'Expand player' : 'Compact player'}
              style={compact
                ? { width: 20, height: 20, minWidth: 20, padding: 0, fontSize: 11, opacity: 0.7 }
                : { opacity: 0.7 }
              }
            />
          </Tooltip>
        </div>
      )}

      {/* Tomorrow.io Logo */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: compact ? 5 : 8,
          flexShrink: 0,
          backgroundColor: isDark ? '#1a1a1a' : '#fff',
          padding: compact ? '0 6px' : '0 10px',
          borderRadius: compact ? 3 : 4,
          border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'}`,
          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.06)',
          height: sectionHeight,
          boxSizing: 'border-box',
        }}
      >
        <img
          src="/tio-ball.svg"
          alt=""
          style={{
            height: compact ? 18 : 28,
            width: compact ? 18 : 28,
            animation: loading
              ? 'dashboard-ball-spin 1.2s linear infinite'
              : 'none',
          }}
        />
        {!compact && (
          <img
            src={isDark ? '/tio-text-white.svg' : '/tio-text-dark.svg'}
            alt="Tomorrow.io"
            style={{ height: 22, opacity: 0.9 }}
          />
        )}
      </div>
    </div>
  );
};
